import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';

import { SystemMetadata } from '../utils/system-metadata.util';

export type { SystemMetadata };

export interface User {
  id?: string;
  uid?: string;
  name: string;
  email: string;
  phoneNumber?: string | null;
  profileImage?: string | null;
  isEmailVerified: boolean;
  isActive: boolean;
  signUpMethod: "email-password" | "google" | "facebook" | "admin-created";
  role?: "super-admin" | "company-admin" | "manager" | "staff" | "auditor";
  roleId?: string;
  lastLoginAt?: Date | null;
  __system?: SystemMetadata;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  phoneNumber?: string;
  profileImage?: string;
  role?: string;
  roleId?: string;
  companyId?: string;
  companyRole?: string;
  __system?: SystemMetadata;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private api = inject(ApiService);

  async getAll(filters?: {
    search?: string;
    role?: string;
    status?: string;
    verified?: string;
    companyId?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{ items: User[]; nextCursor?: string | null; hasMore?: boolean; total?: number }> {
    const params = new URLSearchParams();
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.cursor) params.append('cursor', filters.cursor);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.verified) params.append('verified', filters.verified);
    if (filters?.companyId) params.append('companyId', filters.companyId);
    
    const queryString = params.toString();
    const url = queryString ? `/users?${queryString}` : '/users';
    const response = await this.api.get<{ success: boolean; items: User[]; nextCursor?: string | null; hasMore?: boolean; total?: number }>(url);
    return { 
      items: response.items || [], 
      nextCursor: response.nextCursor || null,
      hasMore: response.hasMore || false,
      total: response.total,
    };
  }

  async getById(id: string): Promise<User | null> {
    const response = await this.api.get<{ success: boolean } & User>(`/users/${id}`);
    // Response might be the user object directly or wrapped in success
    if (response && 'uid' in response) {
      return response as User;
    }
    return null;
  }

  async create(user: CreateUserRequest): Promise<{ success: boolean; uid?: string }> {
    const response = await this.api.post<{ success: boolean; uid: string }>('/admin/create-user', user);
    return { success: response.success, uid: response.uid };
  }

  async update(id: string, user: Partial<User>): Promise<{ success: boolean }> {
    const response = await this.api.put<{ success: boolean }>(`/users/${id}`, user);
    return response;
  }

  async delete(id: string): Promise<{ success: boolean }> {
    const response = await this.api.delete<{ success: boolean }>(`/users/${id}`);
    return response;
  }

  async getUserCompanies(uid: string, companyId?: string): Promise<{ companyId: string; companyName: string; role: string; assignedAt: number }[]> {
    const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const response = await this.api.get<{ success: boolean; companies: any[] }>(`/admin/user-companies/${uid}${qs}`);
    return response.companies || [];
  }

  async assignCompanyToUser(uid: string, companyId: string, role: string): Promise<{ success: boolean }> {
    const response = await this.api.post<{ success: boolean }>('/admin/assign-company-role', {
      uid,
      companyId,
      role
    });
    return response;
  }

  async removeUserFromCompany(uid: string, companyId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete<{ success: boolean }>(`/admin/user-companies/${uid}/${companyId}`);
    return response;
  }
}

